import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../../hooks/use-session.hook';
import useModal from '../../../hooks/use-close-modal.hook';
import { PROFILES } from '../../../constants';
import { RolesService } from '../../../services/roles.service';
import { AlertService } from '../../../services/alert.service';
import { CompaniesService } from '../../../services/companies.service';
import { AccountsService } from '../../../services/accounts.service';
import { UsersService } from '../../../services/users.service';
import InputCopyClipboard from '../../../components/forms/clipboard-copy.component';
import LogData from '../../../components/logs/log-data.component';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import Button from '../../../components/forms/button.component';
import Title from '../../../components/forms/titles.component';
import Select from 'react-select';
import Input from '../../../components/forms/input.component';
import Icon from '../../../components/forms/icon.component';
interface AddUserProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function AddUser({ show, onRequestClose, payload }: AddUserProps) {
  const { session, selectedAccount } = useSession();
  const { t } = useTranslation();
  const emailRegex =
    /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const [roles, setRoles] = useState<any>([]);
  const [companies, setCompanies] = useState<any>([]);
  const [accounts, setAccounts] = useState<any>([]);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const defaultValues = {
    _id: '',
    username: '',
    full_name: '',
    email: '',
    profile: '',
    role: '',
    company: '',
    account: ''
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const resetValues = () => {
    setCompanies([]);
    setRoles([]);
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/\s+/g, '_');
    setValue('username', value, { shouldValidate: true });
  };

  const [profileOptions, setProfileOptions] = useState([
    { value: PROFILES.ROOT, label: t('users-page.root-profile') },
    { value: PROFILES.ADMIN, label: t('users-page.admin-profile') },
    { value: PROFILES.STANDARD_USER, label: t('users-page.standard-user-profile') }
  ]);

  const getRoles = async (selectedAccount: any) => {
    try {
      setRoles([]);
      const selectedProfile = watch('profile');
      let data: any[] = [];

      if (selectedProfile === PROFILES.ROOT) {
        data = await RolesService.getRoles();
        data = data.filter((item: any) => item.profile === PROFILES.ROOT && item.status === true);
      } else {
        if (!selectedAccount) return;

        data = await RolesService.getRolesForAccount({ accountId: selectedAccount });

        data = data.filter((item: any) => {
          const profileMatches =
            selectedProfile === PROFILES.STANDARD_USER || selectedProfile === PROFILES.AGENTE
              ? item.profile === selectedProfile.toUpperCase()
              : true;
          return profileMatches && item.status === true;
        });
      }

      const mappedRoles = data.map((item: any) => ({
        value: item._id,
        label: item.name
      }));

      setRoles(mappedRoles);
    } catch (errorCode: any) {
      console.error(errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      const mappedCompanies = data.map((item: any) => ({
        value: item._id,
        label: item.name
      }));
      setCompanies(mappedCompanies);
    } catch (errorCode: any) {
      console.log(errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getAccounts = async (selectedCompany: any) => {
    try {
      const data = await AccountsService.getAccountsForCompany(selectedCompany);
      const mappedAccounts = data.map((item: any) => ({
        value: item._id,
        label: item.name
      }));
      setAccounts(mappedAccounts);
    } catch (errorCode: any) {
      console.log(errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const submit = async (data: any, buttonId: string) => {
    let response;
    try {
      const isConfirmed = await AlertService.warning(
        t('general.atention'),
        payload != null
          ? `${t('users-page.modal-add-user.edit-warning')}, ${payload?.username}`
          : `${t('users-page.modal-add-user.add-warning')}, ${data.username}`,
        () => Promise.resolve(true)
      );
      if (!isConfirmed) return;
      if (payload != null) {
        data = RequestBody(data);
        await UsersService.editUser(data);
        AlertService.toastSuccess(t('users-page.edit-user-success'));
      } else {
        delete data._id;
        data = RequestBody(data);
        response = await UsersService.saveUser(data);
        AlertService.toastSuccess(t('users-page.add-user-success'));
      }
      onRequestClose(true);
      if (!payload) {
        const html = (
          <div>
            <p>{t('users-page.modal-add-user.password')}</p>
            <div className="width-fit-content m-auto">
              <InputCopyClipboard text={response?.password} />
            </div>
          </div>
        );
        await AlertService.success(
          <p className="h2">{t('users-page.modal-add-user.password-label-success')}</p>,
          html
        );
      }
      reset(defaultValues);
      resetValues();
      response = null;
      LogData(data, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log(errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const closeModal = () => {
    onRequestClose(false);
    reset(defaultValues);
    setRoles([]);
    setCompanies([]);
    setAccounts([]);
    resetValues();
  };

  const RequestBody = (data: any) => {
    delete data.company;
    data.profile === PROFILES.ADMIN && delete data.role;
    if (data.profile === PROFILES.ROOT) {
      delete data.account;
      if (watch('profile') === PROFILES.ROOT && !watch('role')) {
        delete data.role;
      }
    }
    if (data.account && data.profile === PROFILES.ADMIN) {
      data.accounts = data.account;
      delete data.account;
    }
    if (data.role && typeof data.role === 'object' && 'value' in data.role) {
      data.role = data.role.value;
    }
    if (payload && data.profile === PROFILES.ROOT && !data.role) {
      delete data.role;
    }
    return data;
  };

  useEffect(() => {
    getCompanies();
    const baseOptions = [
      { value: PROFILES.ROOT, label: t('users-page.root-profile') },
      { value: PROFILES.ADMIN, label: t('users-page.admin-profile') },
      { value: PROFILES.STANDARD_USER, label: t('users-page.standard-user-profile') }
    ];
    const filteredOptions =
      session.profile !== PROFILES.ROOT
        ? baseOptions.filter((option) => option.value !== PROFILES.ROOT)
        : baseOptions;
    setProfileOptions(filteredOptions);
    if (session.profile === PROFILES.ADMIN && selectedAccount?.account_id) {
      setValue('account', selectedAccount.account_id);
    }
  }, [show]);

  useEffect(() => {
    const selectedCompany = watch('company');
    if (selectedCompany) {
      getAccounts(selectedCompany);
      setValue('account', '');
      setValue('role', '');
      setSelectedRole(null);
    }
  }, [watch('company')]);

  useEffect(() => {
    const profile = watch('profile');
    const account = watch('account');

    if (profile && account && profile !== PROFILES.ADMIN) {
      getRoles(account);
    }
    if (profile === PROFILES.ROOT) {
      getRoles(null);
    }
  }, [watch('profile'), watch('account')]);

  useEffect(() => {
    if (!payload || roles.length === 0) {
      setSelectedRole(null);
      setValue('role', defaultValues.role);
      return;
    }
    let roleId = '';
    if (payload.profile === PROFILES.ROOT) {
      roleId = payload.role?._id || '';
    } else if (payload.profile === PROFILES.STANDARD_USER) {
      roleId = payload.accounts?.[0]?.role?._id || payload.role?._id || '';
    } else {
      roleId = payload.role?._id || '';
    }

    const roleOption = roles.find((role: any) => role.value === roleId) || null;
    setSelectedRole(roleOption);
    setValue('role', roleOption, { shouldValidate: true });
  }, [roles, payload]);

  useEffect(() => {
    if (!payload) return;
    getCompanies();
    const companyId =
      payload.profile === PROFILES.ADMIN
        ? payload.accounts?.[0]?.account?.company?._id
        : payload.accounts?.[0]?.account?.company?._id;

    reset({
      _id: payload._id || defaultValues._id,
      username: payload.username || defaultValues.username,
      full_name: payload.full_name || defaultValues.full_name,
      email: payload.email || defaultValues.email,
      profile: payload.profile || defaultValues.profile,
      account:
        payload.profile === PROFILES.ADMIN
          ? payload.accounts?.map((acc: any) => acc.account?._id).filter(Boolean) || []
          : payload.accounts?.[0]?.account?._id || '',
      company: payload.accounts?.[0]?.account?.company?._id || defaultValues.company,
      role:
        payload?.profile === PROFILES.STANDARD_USER || payload?.profile === PROFILES.ROOT
          ? payload?.role?._id || payload?.accounts?.[0]?.role?._id || ''
          : ''
    });

    if (companyId) {
      getAccounts(companyId).then(() => {
        if (payload.profile === PROFILES.ADMIN) {
          setValue(
            'account',
            payload.accounts?.map((acc: any) => acc.account?._id).filter(Boolean) || [],
            { shouldValidate: true }
          );
        } else if (payload.accounts?.[0]?.account?._id) {
          setValue('account', payload.accounts?.[0]?.account?._id);
        }
      });
    }

    if (payload.profile !== PROFILES.ADMIN) {
      getRoles(payload.accounts?.[0]?.account?.company?._id);
    }
  }, [payload]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-1000px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => closeModal()}
            >
              <i className="ki-duotone ki-cross fs-2x">
                <span className="path1" />
                <span className="path2" />
              </i>
            </div>
          </div>
          <form
            id="addUserForm"
            onSubmit={(e) => {
              e.preventDefault();
              const buttonID = watch('_id') ? 'editUser-73264734545' : 'addUser-33949202';
              handleSubmit((data) => submit(data, buttonID))(e);
            }}
          >
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {watch('_id') ? (
                    <>
                      {t('users-page.modal-add-user.edit-user-title')}
                      <a className="link-primary fw-bold ms-2" href="#">
                        {payload?.username}
                      </a>
                    </>
                  ) : (
                    <> {t('users-page.modal-add-user.add-user-title')}</>
                  )}
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row mt-10">
                <div className="col-lg-6 mb-10 mb-lg-0  h-100 p-10">
                  <Title
                    title={t('users-page.modal-add-user.data-user-title')}
                    subtitle={t('users-page.modal-add-user.data-user-text')}
                    icon="ki-notepad-edit"
                  />
                  <div className="fv-row mb-7">
                    <Input
                      label={t('users-page.modal-add-user.full-name-label')}
                      requiredIndicator="required"
                      placeholder={t('users-page.modal-add-user.full-name-placeholder')}
                      errors={errors.full_name}
                      {...register('full_name', {
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>
                  <div className="fv-row mb-7">
                    <Input
                      label={t('users-page.modal-add-user.user-name-label')}
                      requiredIndicator="required"
                      placeholder={t('users-page.modal-add-user.user-name-placeholder')}
                      errors={errors.username}
                      {...register('username', {
                        onChange: handleUserChange,
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>
                  <div className="fv-row mb-7">
                    <Input
                      label={t('users-page.modal-add-user.email-label')}
                      requiredIndicator="required"
                      placeholder={t('users-page.modal-add-user.email-placeholder')}
                      errors={errors.email}
                      {...register('email', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: emailRegex,
                          message: t('account-page.error-email')
                        }
                      })}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-10 mb-lg-0  h-100 p-10">
                  <Title
                    title={t('users-page.modal-add-user.config-user-title')}
                    subtitle={t('users-page.modal-add-user.config-user-subtitle')}
                    icon="ki-setting-2"
                    type="info"
                  />
                  <div className="col">
                    <div className="fv-row mb-8">
                      <label className="form-label required">
                        {t('users-page.modal-add-user.profile-label')}
                      </label>
                      <Controller
                        control={control}
                        name="profile"
                        rules={{ required: true }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <>
                            <Select
                              options={profileOptions}
                              isDisabled={payload}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue
                                  ? t('general.no-options', { inputValue })
                                  : t('general.no-options')
                              }
                              className={
                                error ? 'form-select-custom is-invalid' : 'form-select-custom'
                              }
                              classNamePrefix="form-select-custom"
                              placeholder={t('users-page.modal-add-user.profile-placeholder')}
                              onChange={(
                                selectedOption: { value: string; label: string } | null
                              ) => {
                                onChange(selectedOption ? selectedOption.value : null);
                                setValue('profile', selectedOption ? selectedOption.value : '');
                              }}
                              value={
                                profileOptions.find(
                                  (option: { value: string; label: string }) =>
                                    option.value === value
                                ) || null
                              }
                            />
                            <InvalidFeedback error={error} />
                          </>
                        )}
                      />
                    </div>
                  </div>
                  {session.profile === PROFILES.ROOT &&
                    (watch('profile') === PROFILES.ADMIN ||
                      watch('profile') === PROFILES.AGENTE ||
                      watch('profile') === PROFILES.STANDARD_USER) && (
                      <div className="row">
                        <div className="col">
                          <div className="fv-row mb-7">
                            <label className="form-label required">
                              {t('users-page.modal-add-user.company-label')}
                            </label>
                            <Controller
                              control={control}
                              name="company"
                              rules={{ required: true }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <>
                                  <Select
                                    options={companies}
                                    noOptionsMessage={({ inputValue }) =>
                                      inputValue
                                        ? t('general.no-options', { inputValue })
                                        : t('general.no-options')
                                    }
                                    className={
                                      error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                    }
                                    classNamePrefix="form-select-custom"
                                    placeholder={t('users-page.modal-add-user.company-placeholder')}
                                    onChange={(
                                      selectedOption: { value: string; label: string } | null
                                    ) => {
                                      onChange(selectedOption ? selectedOption.value : null);
                                    }}
                                    value={
                                      companies.find((company: any) => company.value === value) ||
                                      null
                                    }
                                  />
                                  <InvalidFeedback error={error} />
                                </>
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  {session.profile === PROFILES.ROOT &&
                    watch('profile') !== '' &&
                    watch('profile') !== PROFILES.ROOT && (
                      <div className="row">
                        <div className="col">
                          <div className="fv-row mb-7">
                            <label className="form-label required">
                              {t('roles.account-label')}
                            </label>
                            <Controller
                              control={control}
                              name="account"
                              rules={{ required: true }}
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <>
                                  <Select
                                    options={accounts}
                                    isMulti={watch('profile') === PROFILES.ADMIN}
                                    noOptionsMessage={({ inputValue }) =>
                                      inputValue
                                        ? t('general.no-options', { inputValue })
                                        : t('general.no-options')
                                    }
                                    className={
                                      error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                    }
                                    classNamePrefix="form-select-custom"
                                    placeholder={t('roles.account-placeholder')}
                                    onChange={(selectedOption) => {
                                      if (watch('profile') === PROFILES.ADMIN) {
                                        onChange(
                                          selectedOption
                                            ? selectedOption.map((option: any) => option.value)
                                            : []
                                        );
                                      } else {
                                        onChange(selectedOption ? selectedOption.value : null);
                                      }
                                    }}
                                    value={
                                      watch('profile') === PROFILES.ADMIN
                                        ? accounts.filter((account: any) =>
                                            (value || []).includes(account.value as never)
                                          )
                                        : accounts.find(
                                            (account: any) => account.value === value
                                          ) || null
                                    }
                                  />
                                  <InvalidFeedback error={error} />
                                </>
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  {watch('profile') !== PROFILES.ADMIN && watch('profile') !== '' && (
                    <div className="fv-row mb-7">
                      <label
                        className={`form-label ${watch('profile') !== PROFILES.ROOT ? 'required' : ''}`}
                      >
                        {t('users-page.modal-add-user.rol-label')}
                        {watch('profile') === PROFILES.ROOT && (
                          <span className="fs-8 text-muted ms-1">({t('general.optional')})</span>
                        )}
                      </label>
                      <Controller
                        control={control}
                        name="role"
                        rules={{ required: watch('profile') !== PROFILES.ROOT ? true : false }}
                        render={({ field: { onChange }, fieldState: { error } }) => (
                          <>
                            <Select
                              options={roles}
                              isDisabled={
                                watch('profile') !== PROFILES.ROOT &&
                                session.profile === PROFILES.ROOT &&
                                !watch('company')
                              }
                              isClearable={watch('profile') === PROFILES.ROOT}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue
                                  ? t('general.no-options', { inputValue })
                                  : t('general.no-options')
                              }
                              className={
                                error ? 'form-select-custom is-invalid' : 'form-select-custom'
                              }
                              classNamePrefix="form-select-custom"
                              placeholder={t('users-page.modal-add-user.rol-placeholder')}
                              value={selectedRole}
                              onChange={(selectedOption) => {
                                setSelectedRole(selectedOption);
                                onChange(selectedOption ? selectedOption.value : '');
                              }}
                            />
                            <InvalidFeedback error={error} />
                          </>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-12">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => closeModal()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                >
                  {t('general.save')}
                  <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddUser;
